import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Spinner,
    Button
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import { FaDownload, FaPrint } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './ViewExam.css';
import Footer from 'components/Footers/Footer';
import {
    createLandscapePdf,
    standardAutoTableOptions,
    drawCenteredFooter,
} from '../../utils/pdfLayout';

const baseURL = apiURL1;

function ViewSubjectResult() {
    const { classId, subjectId, type, year } = useParams();
    const location = useLocation();
    const { className, subjectName, typeName } = location.state || {};

    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    // New totals based on updated API shape
    const [subjectTotalMarks, setSubjectTotalMarks] = useState('-');
    const [componentsTotalMarks, setComponentsTotalMarks] = useState('-');
    const [finalSubjectTotalMarks, setFinalSubjectTotalMarks] = useState('-');
    const [grandTotalMarks, setGrandTotalMarks] = useState('-');
    const [hasFinal, setHasFinal] = useState(false);
    const [schoolInfo, setSchoolInfo] = useState({});
    // Dynamic component column names for this subject
    const [componentNames, setComponentNames] = useState([]);
    const token = localStorage.getItem("token");


    useEffect(() => {
        fetchResults();
        fetchSchoolInfo();
    }, [classId, subjectId, type, year]);

    const fetchSchoolInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            const data = response.data;
            if (data && data.length > 0) {
                setSchoolInfo(data[0]);
            }
        } catch (error) {
            // console.error('Error fetching school info:', error);
        }
    };

    const fetchResults = async () => {
        setLoading(true);
        try {
            let response;
            // Try new route first, then fallback
            try {
                response = await axios.get(
                    `${baseURL}/results/subject/${subjectId}/type/${type}/year/${year}/${classId}`,
                    { headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-store', 'Pragma': 'no-cache' } }
                );
            } catch (primaryErr) {
                response = await axios.get(
                    `${baseURL}/superadmin/results/subject/${subjectId}/type/${type}/year/${year}/${classId}`,
                    { headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-store', 'Pragma': 'no-cache' } }
                );
            }

            if (response.data?.data && response.data.data.length > 0) {
                const resultData = response.data.data[0];
                const studentData = Array.isArray(resultData.students) ? resultData.students : [];

                setSubjectTotalMarks(
                    resultData.midSubjectTotalMarks !== undefined && resultData.midSubjectTotalMarks !== null
                        ? resultData.midSubjectTotalMarks
                        : (resultData.subjectTotalMarks ?? '-')
                );
                const componentsMetaLocal = Array.isArray(resultData.components) ? resultData.components : [];
                const computedComponentsTotal = componentsMetaLocal.reduce((sum, cm) => sum + (Number(cm?.totalMarks) || 0), 0);
                setComponentsTotalMarks(
                    resultData.componentsTotalMarks !== undefined && resultData.componentsTotalMarks !== null
                        ? resultData.componentsTotalMarks
                        : computedComponentsTotal
                );
                setFinalSubjectTotalMarks(
                    resultData.subjectTotalMarksFinal !== undefined && resultData.subjectTotalMarksFinal !== null
                        ? resultData.subjectTotalMarksFinal
                        : '-'
                );
                const hasFinalLocal = (resultData.subjectTotalMarksFinal !== undefined && resultData.subjectTotalMarksFinal !== null)
                    || (studentData.some(s => (s.subjectObtainMarksFinal !== undefined && s.subjectObtainMarksFinal !== null)
                        || (s.subjectTotalMarksFinal !== undefined && s.subjectTotalMarksFinal !== null)));
                setHasFinal(hasFinalLocal);
                const midGrandTotal = (Number(resultData.midSubjectTotalMarks ?? resultData.subjectTotalMarks) || 0) + (Number(resultData.componentsTotalMarks ?? computedComponentsTotal) || 0);
                const finalTotal = hasFinalLocal ? (Number(resultData.subjectTotalMarksFinal) || 0) : 0;
                const combinedGrand = midGrandTotal + finalTotal;
                setGrandTotalMarks(
                    hasFinalLocal
                        ? (resultData.grandTotalMarks !== undefined && resultData.grandTotalMarks !== null
                            ? resultData.grandTotalMarks
                            : (combinedGrand > 0 ? combinedGrand : '-'))
                        : '-'
                );

                const uniqueNames = Array.from(new Set([
                    ...studentData.flatMap(s => (Array.isArray(s.components) ? s.components : []).map(cm => cm?.component?.name).filter(Boolean)),
                    ...studentData.flatMap(s => (Array.isArray(s.componentObtained) ? s.componentObtained : []).map(co => co?.component?.name).filter(Boolean))
                ]));
                setComponentNames(uniqueNames);

                const mappedStudents = studentData.map(s => {
                    const baseStudent = s.student || {};
                    const totalsByName = new Map((Array.isArray(s.components) ? s.components : []).map(cm => [cm?.component?.name, cm?.totalMarks]));
                    const obtainedByName = new Map((Array.isArray(s.componentObtained) ? s.componentObtained : []).map(co => [co?.component?.name, co?.marks]));
                    const reg = baseStudent.registrationNumber || s.registrationNumber;
                    const name = baseStudent.fullName || s.studentName;
                    return {
                        studentId: baseStudent._id || s._id || s.id,
                        registrationNumber: reg || '-',
                        fullName: name || '-',
                        midSubjectObtainedMarks: s.midSubjectObtainedMarks,
                        midSubjectTotalMarks: s.midSubjectTotalMarks ?? resultData.midSubjectTotalMarks ?? '-',
                        subjectObtainMarksFinal: s.subjectObtainMarksFinal,
                        finalSubjectTotalMarks: s.subjectTotalMarksFinal ?? resultData.subjectTotalMarksFinal ?? '-',
                        grandTotal: s.grandTotal ?? '-',
                        grandObtained: s.grandObtained ?? '-',
                        grade: s.grade || '-',
                        remarks: s.remarks || '',
                        totalsByName,
                        obtainedByName
                    };
                });

                setStudents(mappedStudents);
            } else {
                setStudents([]);
                setSubjectTotalMarks('-');
                setComponentsTotalMarks(0);
                setGrandTotalMarks('-');
                toast.warn('No data found for this subject, type, and year.');
            }
        } catch (error) {
            toast.error('Error fetching results');
        } finally {
            setLoading(false);
        }
    };

    const generatePdfReport = () => {
        const pdf = createLandscapePdf(jsPDF);
        const pageWidth = pdf.internal.pageSize.getWidth();

        const img = new Image();
        img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";
        try {
            pdf.addImage(img, 'PNG', 12, 8, 22, 22);
        } catch (e) { /* optional */ }

        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.text(schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College', 40, 14);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(schoolInfo.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.', 40, 20);
        pdf.text(`Tel: ${schoolInfo.schoolPhoneNumber || '+94 67 205 3211'} | Email: ${schoolInfo.schoolEmail || 'info@ummudhardha.lk'}`, 40, 25);

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Subject Result — ${subjectName} (${typeName} ${year})`, pageWidth / 2, 34, { align: 'center' });

        let y = 42;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Class: ${className}  |  Mid Total: ${subjectTotalMarks}  |  Components: ${componentsTotalMarks}${hasFinal ? `  |  Final Total: ${finalSubjectTotalMarks}` : ''}  |  Grand: ${grandTotalMarks}`, 12, y);
        y += 8;

        const tableData = students.map((student) => {
            const hasRowFinal = (student.subjectObtainMarksFinal !== undefined && student.subjectObtainMarksFinal !== null) ||
                (student.finalSubjectTotalMarks !== undefined && student.finalSubjectTotalMarks !== null);
            return [
                student.registrationNumber,
                student.fullName,
                `${student.midSubjectObtainedMarks ?? '-'}/${student.midSubjectTotalMarks ?? '-'}`,
                hasRowFinal ? `${student.subjectObtainMarksFinal ?? '-'}/${student.finalSubjectTotalMarks ?? '-'}` : '-/-',
                hasRowFinal ? (student.grandTotal ?? '-') : (student.grandTotal ?? '-'),
                hasRowFinal ? (student.grandObtained ?? '-') : (student.grandObtained ?? '-'),
                student.grade || '-',
            ];
        });

        pdf.autoTable(standardAutoTableOptions({
            head: [['Reg No.', 'Name', 'Mid', 'Final', 'Total', 'Obtained', 'Grade']],
            body: tableData,
            startY: y,
            columnStyles: {
                0: { cellWidth: 28 },
                1: { cellWidth: 65, halign: 'left' },
                2: { cellWidth: 28 },
                3: { cellWidth: 28 },
                4: { cellWidth: 24 },
                5: { cellWidth: 26 },
                6: { cellWidth: 20 },
            },
        }));

        drawCenteredFooter(pdf, schoolInfo.schoolName);
        pdf.save(`subject-result-${className}-${subjectName}-${typeName}-${year}.pdf`);
    };

    const handlePrint = () => {
        const tableStyle = `border-collapse: collapse; width: 100%; margin: 10px 0;`;
        const thStyle = `background-color: #2980b9; color: white; padding: 8px; text-align: left; border: 1px solid #ddd;`;
        const tdStyle = `padding: 8px; text-align: left; border: 1px solid #ddd;`;

        const printContent = `
            <html>
            <head>
                <title>Subject Result - ${className} - ${subjectName} (${typeName} ${year})</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; margin-bottom: 20px; }
                    .school-name { font-size: 24px; font-weight: bold; }
                    .school-info { font-size: 14px; }
                    .result-title { font-size: 20px; margin-bottom: 10px; }
                    table { ${tableStyle} }
                    th { ${thStyle} }
                    td { ${tdStyle} }
                    .footer { text-align: center; margin-top: 20px; font-size: 12px; }
                    div { margin-bottom: 5px; }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" alt="School Logo" width="100"/>
                    <div class="school-name">${schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College'}</div>
                    <div class="school-info">
                        ${schoolInfo.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.'}<br/>
                        Tel: ${schoolInfo.schoolPhoneNumber || '+94 67 205 3211'} | Email: ${schoolInfo.schoolEmail || 'info@ummudhardha.lk'}
                    </div>
                </div>
                <div class="result-title">Subject Result - ${className} - ${subjectName} (${type} ${year})</div>
                <div><strong>Class:</strong> ${className}</div>
                <div><strong>Subject:</strong> ${subjectName}</div>
                <div><strong>Subject Total Marks (Mid):</strong> ${subjectTotalMarks}</div>
                <div><strong>Components Total Marks (Mid):</strong> ${componentsTotalMarks}</div>
                ${hasFinal ? `<div><strong>Final Subject Total Marks:</strong> ${finalSubjectTotalMarks}</div>` : ''}
                <div><strong>Grand Total Marks:</strong> ${grandTotalMarks}</div>
                
                <h3>Student Results</h3>
                <table border="1">
                    <thead>
                        <tr>
                            <th>Reg Number</th>
                            <th>Name</th>
                            <th>Subject Mid Marks</th>
                            ${componentNames.map(name => `<th>${name}</th>`).join('')}
                            <th>Subject Final Marks</th>
                            <th>Total Marks</th>
                            <th>Total Obtained</th>
                            <th>Grade</th>
                            <th>Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${students.map(student => `
                            <tr>
                                <td>${student.registrationNumber || '-'}</td>
                                <td>${student.fullName || '-'}</td>
                                <td>${`${(student.midSubjectObtainedMarks ?? '-')}/${(student.midSubjectTotalMarks ?? '-')}`}</td>
                                ${componentNames.map(name => {
            const obtained = student.obtainedByName?.get(name) ?? '-';
            const total = student.totalsByName?.get(name) ?? '-';
            return `<td>${obtained}/${total}</td>`;
        }).join('')}
                                <td>${(() => {
                const obt = student.subjectObtainMarksFinal;
                const tot = student.finalSubjectTotalMarks;
                const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                return hasRowFinal ? `${(obt ?? '-')}/${(tot ?? '-')}` : '-/-';
            })()}</td>
                                <td>${(() => {
                const obt = student.subjectObtainMarksFinal;
                const tot = student.finalSubjectTotalMarks;
                const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                return hasRowFinal ? (student.grandTotal ?? '-') : '-';
            })()}</td>
                                <td>${(() => {
                const obt = student.subjectObtainMarksFinal;
                const tot = student.finalSubjectTotalMarks;
                const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                return hasRowFinal ? (student.grandObtained ?? '-') : '-';
            })()}</td>
                                <td>${(() => {
                const obt = student.subjectObtainMarksFinal;
                const tot = student.finalSubjectTotalMarks;
                const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                return hasRowFinal ? (student.grade ?? '-') : '-';
            })()}</td>
                                <td>${(() => {
                const obt = student.subjectObtainMarksFinal;
                const tot = student.finalSubjectTotalMarks;
                const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                return hasRowFinal ? (student.remarks ?? '') : '-';
            })()}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <div class="footer">Generated on: ${new Date().toLocaleDateString()} | ${schoolInfo.schoolName || 'Ummu Dhardha Ladies Islamic College'}</div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    if (loading) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Subject Result</h3>
                                <div>
                                    <Button color="success" size="sm" onClick={generatePdfReport} style={{ marginRight: '10px' }}>
                                        <FaDownload /> Download PDF
                                    </Button>
                                    <Button color="info" size="sm" onClick={handlePrint}>
                                        <FaPrint /> Print
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <h4>Class: {className}</h4>
                                <h4>Subject: {subjectName}</h4>
                                <h4>Subject Total Marks (Mid): {subjectTotalMarks}</h4>
                                <h4>Components Total Marks (Mid): {componentsTotalMarks}</h4>
                                {hasFinal && (
                                    <h4>Final Subject Total Marks: {finalSubjectTotalMarks}</h4>
                                )}
                                <h4>Grand Total Marks: {grandTotalMarks}</h4>

                                <div className="print-only">
                                    <h4>Class: {className}</h4>
                                    <h4>Subject: {subjectName}</h4>
                                    <h4>Subject Total Marks (Mid): {subjectTotalMarks}</h4>
                                    <h4>Components Total Marks (Mid): {componentsTotalMarks}</h4>
                                    {hasFinal && (
                                        <h4>Final Subject Total Marks: {finalSubjectTotalMarks}</h4>
                                    )}
                                    <h4>Grand Total Marks: {grandTotalMarks}</h4>
                                </div>

                                {students.length === 0 ? (
                                    <p>No result found for this subject.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <Table className="table">
                                            <thead>
                                                <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                                    <th>Picture</th>
                                                    <th>Reg Number</th>
                                                    <th>Name</th>
                                                    <th>Subject Mid Marks</th>
                                                    {componentNames.map(name => (
                                                        <th key={`comp-h-${name}`}>{name}</th>
                                                    ))}
                                                    <th>Subject Final Marks</th>
                                                    <th>Total Marks</th>
                                                    <th>Total Obtained</th>
                                                    <th>Grade</th>
                                                    <th>Comments</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {students.map(student => (
                                                    <tr key={student.studentId || student._id}>
                                                        <td>
                                                            {student.picture ? (
                                                                <img
                                                                    src={student.picture}
                                                                    alt="Profile"
                                                                    style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                                                                />
                                                            ) : (
                                                                <div style={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#ddd',
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    No Image
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>{student.registrationNumber || '-'}</td>
                                                        <td>{student.fullName || '-'}</td>
                                                        <td>{`${(student.midSubjectObtainedMarks ?? '-')}/${(student.midSubjectTotalMarks ?? '-')}`}</td>
                                                        {componentNames.map(name => {
                                                            const obtained = student.obtainedByName?.get(name) ?? '-';
                                                            const total = student.totalsByName?.get(name) ?? '-';
                                                            return (
                                                                <td key={`${(student.studentId || student._id)}-comp-${name}`}>{`${obtained}/${total}`}</td>
                                                            );
                                                        })}
                                                        <td>{(() => {
                                                            const obt = student.subjectObtainMarksFinal;
                                                            const tot = student.finalSubjectTotalMarks;
                                                            const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                                                            return hasRowFinal ? `${(obt ?? '-')}/${(tot ?? '-')}` : '-/-';
                                                        })()}</td>
                                                        <td>{(() => {
                                                            const obt = student.subjectObtainMarksFinal;
                                                            const tot = student.finalSubjectTotalMarks;
                                                            const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                                                            return hasRowFinal ? (student.grandTotal ?? '-') : '-';
                                                        })()}</td>
                                                        <td>{(() => {
                                                            const obt = student.subjectObtainMarksFinal;
                                                            const tot = student.finalSubjectTotalMarks;
                                                            const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                                                            return hasRowFinal ? (student.grandObtained ?? '-') : '-';
                                                        })()}</td>
                                                        <td>{(() => {
                                                            const obt = student.subjectObtainMarksFinal;
                                                            const tot = student.finalSubjectTotalMarks;
                                                            const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                                                            return hasRowFinal ? (student.grade ?? '-') : '-';
                                                        })()}</td>
                                                        <td>{(() => {
                                                            const obt = student.subjectObtainMarksFinal;
                                                            const tot = student.finalSubjectTotalMarks;
                                                            const hasRowFinal = (obt !== undefined && obt !== null) || (tot !== undefined && tot !== null);
                                                            return hasRowFinal ? (student.remarks ?? '') : '-';
                                                        })()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>
        </>
    );
}

export default ViewSubjectResult;
